import { ArrowLeftOutlined, FileTextOutlined, PlusOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Button, Card, Flex, Pagination, Skeleton, Typography } from 'antd'
import { PropertyActivityItem, PtoItemType, SearchPropertyActivityParams } from 'Modules'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { AppAdmin } from 'api/app-admin'
import { FEATURE } from 'constants/feature'
import { useDebounce } from 'hooks/use-debounce'
import { usePermission } from 'hooks/use-permission'
import useSearchParams from 'hooks/use-search-params'
import { ActivityCard } from 'modules/property-activities/activity-card'
import { ActivityFormModal } from 'modules/property-activities/activity-form-modal'
import { ActivityListFilter } from 'modules/property-activities/activity-list-filter'
import { DeleteActivityModal } from 'modules/property-activities/delete-activity-modal'
import { ViewActivityModal } from 'modules/property-activities/view-activity-modal'
import { ROUTES } from 'routes'

import './styles.scss'

const { Text } = Typography

const getPtoLocation = (pto: PtoItemType | null | undefined): string => {
  if (!pto) return '-'
  return pto.propertyItems?.location || pto.country || '-'
}

const fetchPto = async (ptoId: string) => {
  const res = await AppAdmin.getPTODetail(ptoId)
  return (res.data?.data ?? null) as PtoItemType | null
}

const fetchActivities = async (params: SearchPropertyActivityParams) => {
  const sortedParams: SearchPropertyActivityParams = {
    ...params,
    sort: params.sort ?? 'timeline:desc',
  }
  const response = await AppAdmin.getPropertyActivities(sortedParams)
  return {
    results: (response.data?.data?.results ?? []) as PropertyActivityItem[],
    total: response.data?.data?.totalResults ?? 0,
  }
}

const PropertyActivitiesDetail = () => {
  const navigate = useNavigate()
  const { ptoId = '' } = useParams<{ ptoId: string }>()
  const { canCRUD } = usePermission()
  const canCreate = canCRUD(FEATURE.PROPERTY_ACTIVITIES)

  const { searchParams, setSearchParams } = useSearchParams<SearchPropertyActivityParams>({
    ptoId,
  } as SearchPropertyActivityParams)

  const [searchText, setSearchText] = useState('')
  const debouncedSearchText = useDebounce(searchText, 300)

  useEffect(() => {
    setSearchParams((pre) => ({
      ...pre,
      ptoId,
      keyword: debouncedSearchText || undefined,
      page: 1,
    }))
  }, [debouncedSearchText, ptoId, setSearchParams])

  const [createOpen, setCreateOpen] = useState(false)
  const [editState, setEditState] = useState<{
    open: boolean
    activity: PropertyActivityItem | null
  }>({ open: false, activity: null })
  const [viewState, setViewState] = useState<{
    open: boolean
    activity: PropertyActivityItem | null
  }>({ open: false, activity: null })
  const [deleteState, setDeleteState] = useState<{
    open: boolean
    activity: PropertyActivityItem | null
  }>({ open: false, activity: null })

  const { data: pto, isLoading: loadingPto } = useQuery({
    queryKey: ['propertyActivities', 'pto', ptoId],
    queryFn: () => fetchPto(ptoId),
    enabled: !!ptoId,
  })

  const { data, isLoading } = useQuery({
    queryKey: ['propertyActivities', 'activities', ptoId, searchParams],
    queryFn: () => fetchActivities(searchParams),
    enabled: !!ptoId,
  })

  const activities = useMemo(() => data?.results ?? [], [data])
  const total = data?.total ?? 0

  return (
    <Flex vertical gap={16} className="property-activities">
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(ROUTES.PROPERTY_ACTIVITIES)}
        style={{ alignSelf: 'flex-start', paddingLeft: 0 }}
      >
        Back to PTOs
      </Button>

      <Flex justify="space-between" align="flex-start">
        <Flex vertical gap={4}>
          {loadingPto ? (
            <Skeleton.Input active size="large" />
          ) : (
            <>
              <div className="main-title">{pto?.fullName ?? '-'}</div>
              <Text type="secondary">{getPtoLocation(pto)}</Text>
            </>
          )}
        </Flex>
        {canCreate && (
          <Button
            color="primary"
            variant="solid"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => setCreateOpen(true)}
          >
            Create Activity
          </Button>
        )}
      </Flex>

      <ActivityListFilter searchText={searchText} onSearchChange={setSearchText} />

      {isLoading ? (
        <Flex vertical gap={12}>
          {[1, 2, 3].map((k) => (
            <Card key={k}>
              <Skeleton active />
            </Card>
          ))}
        </Flex>
      ) : activities.length === 0 ? (
        <Card className="property-activities__empty-card" styles={{ body: { padding: 48 } }}>
          <Flex vertical align="center" gap={8}>
            <FileTextOutlined style={{ fontSize: 48, color: '#9ca3af' }} />
            <Text strong style={{ fontSize: 16 }}>
              No activities found
            </Text>
            <Text type="secondary">Create your first activity to get started</Text>
          </Flex>
        </Card>
      ) : (
        <Flex vertical gap={12}>
          {activities.map((activity) => (
            <ActivityCard
              key={activity._id}
              activity={activity}
              onView={(a) => setViewState({ open: true, activity: a })}
              onEdit={(a) => setEditState({ open: true, activity: a })}
              onDelete={(a) => setDeleteState({ open: true, activity: a })}
            />
          ))}
        </Flex>
      )}

      {total > (searchParams.limit ?? 10) && (
        <Flex justify="flex-end">
          <Pagination
            current={searchParams.page}
            pageSize={searchParams.limit}
            total={total}
            onChange={(page, pageSize) => {
              setSearchParams((pre) => ({ ...pre, page, limit: pageSize }))
            }}
            showSizeChanger
          />
        </Flex>
      )}

      <ActivityFormModal
        open={createOpen}
        ptoId={ptoId}
        ptoName={pto?.fullName}
        onClose={() => setCreateOpen(false)}
      />

      <ActivityFormModal
        open={editState.open}
        ptoId={ptoId}
        ptoName={pto?.fullName}
        activity={editState.activity}
        onClose={() => setEditState({ open: false, activity: null })}
      />

      <ViewActivityModal
        open={viewState.open}
        activity={viewState.activity}
        onClose={() => setViewState({ open: false, activity: null })}
      />

      <DeleteActivityModal
        open={deleteState.open}
        activity={deleteState.activity}
        ptoId={ptoId}
        onClose={() => setDeleteState({ open: false, activity: null })}
      />
    </Flex>
  )
}

export default PropertyActivitiesDetail
